// Single source of truth for turning internal values into customer-facing
// labels. Anywhere a raw id, code, or snake_case key would otherwise leak
// into the UI / PDF / DOCX, route it through one of these helpers.
//
// Editing rules: every helper must be safe with unknown inputs — fall back
// to the raw value rather than throwing. Display strings only; never logic.

import type { ConsolidationMode } from "../shared/types";

// Framework evidence_scale names (from framework/ai_era_cyber_defense_model.md
// front-matter). Indexed 0-5 inclusive. Half-levels are rounded to the
// nearer band; floor/ceil bands are joined with a slash when ambiguous.
const EVIDENCE_SCALE: Record<number, string> = {
  0: "Not Present",
  1: "Ad Hoc",
  2: "Developing",
  3: "Established",
  4: "Measured",
  5: "Optimizing",
};

const STAGE_NAMES: Record<number, string> = {
  1: "Foundational",
  2: "Standardized",
  3: "Orchestrated",
  4: "Autonomous",
  5: "Frontier-Ready",
};

const DOMAIN_SHORT: Record<string, string> = {
  D1: "Resilient Infra",
  D2: "Modernize",
  D3: "Elevate Posture",
};

const SEVERITY_FULL: Record<string, "Critical" | "High" | "Medium" | "Low"> = {
  Critical: "Critical",
  High: "High",
  Medium: "Medium",
  Low: "Low",
  C: "Critical",
  H: "High",
  M: "Medium",
  L: "Low",
};

// V4.2 (FINDINGS.md F-16): named after what the reader gets, not after the
// arithmetic. "Max" vs "weighted average" told the user which function runs;
// it did not tell them which of the two reports they were about to read.
const CONSOLIDATION_LABEL: Record<ConsolidationMode, string> = {
  max: "Highest risk — report the estate at the level of its most exposed system (recommended)",
  weighted_avg: "Typical risk — report the average across your systems",
};

// V3.4 (#9): plain-English explanation of why the consolidation choice matters.
// Only relevant when more than one use case is in scope.
const CONSOLIDATION_HELP: Record<ConsolidationMode, string> = {
  max: "Each threat is scored at the worst level it reaches in any one of your systems. This is the default because an attacker only needs the weakest one, so it is the view that matches how the estate will actually be breached.",
  weighted_avg:
    "Each threat is averaged over the systems it actually applies to — a threat relevant to two of your five programmes is averaged over those two, not diluted across all five. Useful for describing the typical system; it will understate the risk concentrated in your single most exposed one.",
};

/** `3` → `"L3 — Established"`; non-integer → `"L2.5 — Developing → Established"`. */
export function formatMaturityLevel(level: number | undefined | null): string {
  if (level === undefined || level === null || Number.isNaN(level)) return "—";
  const code = Number.isInteger(level) ? `L${level}` : `L${level.toFixed(1)}`;
  if (Number.isInteger(level)) {
    const name = EVIDENCE_SCALE[level];
    return name ? `${code} — ${name}` : code;
  }
  const lo = Math.floor(level);
  const hi = Math.ceil(level);
  const loName = EVIDENCE_SCALE[lo];
  const hiName = EVIDENCE_SCALE[hi];
  if (loName && hiName) return `${code} — ${loName} → ${hiName}`;
  return code;
}

/** `1` → `"Foundational"` (workstream stage names; distinct from per-control evidence scale). */
export function formatStageName(level: number): string {
  return STAGE_NAMES[Math.round(level)] ?? `Stage ${level}`;
}

/** `"D1"` → `"D1 — Protect"`; pass `name` to override the registered short name. */
export function formatDomain(domainId: string, name?: string): string {
  const short = name ?? DOMAIN_SHORT[domainId];
  if (!short) return domainId;
  // Avoid double "Protect — Securing AI Implementations" when caller passed a long name.
  const compact = short.includes(" — ") ? short.split(" — ")[0] : short;
  return `${domainId} — ${compact}`;
}

/** Look up the option label for a known env dimension value. Lives in
 *  modules/taxonomy/labels.ts, because resolving it needs the taxonomy
 *  catalogue and `shared` must not depend on a feature module.
 *  @see modules/taxonomy/labels.ts */

/** `model_source` → `"Model source"`. */
export function formatEnvKey(key: string): string {
  const spaced = key.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function formatSeverity(severity: string | undefined): string {
  if (!severity) return "—";
  return SEVERITY_FULL[severity] ?? severity;
}

export function formatConsolidationMode(mode: ConsolidationMode): string {
  return CONSOLIDATION_LABEL[mode] ?? mode;
}

// V3.4 (#9): short label for the toggle/heading, and the one-line "why".
export function formatConsolidationShort(mode: ConsolidationMode): string {
  return mode === "max" ? "Highest risk" : "Typical risk";
}

export function formatConsolidationHelp(mode: ConsolidationMode): string {
  return CONSOLIDATION_HELP[mode] ?? "";
}
